import { useMediaQuery } from '@chakra-ui/react';
import BEIcon from '@molecules/BEIcon';
import { usePopover } from '@molecules/BEOffScreen';
import { forwardRef, useState, useMemo, useRef } from 'react';
import { BELangCurrencySelectProps } from './LangCurrencySelectProps';
import {
  StyledGlobeButton,
  StyledCurrencyButton,
  StyledLangCurrencySelect,
} from './StyledLangCurrencySelect';
import ScreenSize from '../../../constants/screen-sizes';
import { mergeRefs } from '@utils/view-layer/react';
import { observer } from 'mobx-react';
import { useStore } from '@data/other/withStore';
import DropdownMenu from '@molecules/FormFactory/Forms/Dropdown/DropdownMenu';

/* eslint-disable react/display-name */
const LangCurrencySelect = forwardRef<
  HTMLInputElement,
  BELangCurrencySelectProps
>((props, ref) => {
  const _ref = useRef(null);

  const { environment } = useStore();
  const { language, currency, setLanguage, setCurrency } = environment;
  const [wrapperRef, setWrapperRef] = useState<any>();

  const [isMobile] = useMediaQuery(`(max-width: ${ScreenSize.MOBILE_LARGE})`);

  const langPopover = usePopover(DropdownMenu, {
    id: 'language',
    referenceElement: wrapperRef,
    renderMethod: isMobile ? 'fullscreen' : 'reference',
    ...(isMobile && { pinTo: 'bottom', hasOverlay: true }),
    width: 347,
  });

  const currPopover = usePopover(DropdownMenu, {
    id: 'currency',
    referenceElement: wrapperRef,
    renderMethod: isMobile ? 'fullscreen' : 'reference',
    ...(isMobile && { pinTo: 'bottom', hasOverlay: true }),
    width: 375,
  });

  const onSelectLang = (v) => {
    langPopover.close();
    setLanguage(v);
  };

  const onSelectCurrency = (v) => {
    currPopover.close();
    setCurrency(v);
  };

  const handleClickLanguage = () => {
    currPopover.close();
    langPopover.open({
      inputRef: mergeRefs(_ref, ref),
      name: 'language',
      placeholder: 'Select Language',
      showHeader: true,
      options: props.languages?.map((lang) => ({ id: lang.value, ...lang })),
      value: language,
      showCheck: true,
      onSelect: onSelectLang,
    });
  };

  const handleClickCurrency = () => {
    langPopover.close();
    currPopover.open({
      inputRef: mergeRefs(_ref, ref),
      name: 'currency',
      options: props.currencies?.map((curr) => ({
        id: curr.value,
        text: `${curr.shortText} ${curr.longText}`,
        value: curr.value,
      })),
      value: currency,
      showCheck: true,
      placeholder: 'Select Currency',
      showHeader: true,
      onSelect: onSelectCurrency,
    });
  };

  const selectedCurrency = useMemo(() => {
    return props.currencies?.find((curr) => curr.value === currency);
  }, [currency, props.currencies]);

  return (
    <StyledLangCurrencySelect ref={setWrapperRef}>
      <StyledGlobeButton
        icon={<BEIcon name="globe" size={props.size} />}
        onClick={handleClickLanguage}
        {...props}
      />
      <StyledCurrencyButton onClick={handleClickCurrency} {...props}>
        {selectedCurrency ? selectedCurrency.shortText : 'USD'}
      </StyledCurrencyButton>
    </StyledLangCurrencySelect>
  );
});

export default observer(LangCurrencySelect);
